/**
 * Phase 1.7 — AppShell.
 *
 * Single layout entry point for every authenticated v2 page. Composes:
 *   Sidebar (left, fixed 240px on md+)
 *   Header (sticky top, holds page title + actions via context)
 *   ReleaseBanner (above content, dismissible)
 *   Main content + optional RightRail (passed by the page)
 *   QuickLogFab (floating, bottom-right)
 *
 * Pages opt into the right rail by passing `rail`. Card-grid pages don't,
 * so they get the full content width. Mobile stacks the rail UNDER the
 * main content rather than alongside (lg breakpoint splits the layout).
 *
 * Pages never render the chrome themselves — they just return their
 * content tree, and AppShell wraps it. This is also where layout_v2 is
 * gated; if the flag is off, the legacy AppLayout is used instead.
 */

import type { ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { Header, HeaderProvider } from './Header';
import { QuickLogFab } from './QuickLogFab';
import { ReleaseBanner } from './ReleaseBanner';
import { cn } from '@/lib/utils';

export interface AppShellProps {
  children: ReactNode;
  rail?: ReactNode;
  fullBleed?: boolean;
}

export function AppShell({ children, rail, fullBleed }: AppShellProps): JSX.Element {
  return (
    <HeaderProvider>
      <div className="min-h-screen bg-sand text-charcoal dark:bg-sand-dark dark:text-charcoal-dark">
        <Sidebar />
        <div className="md:pl-sidebar-w">
          <Header />
          <ReleaseBanner />
          <main
            className={cn(
              'flex flex-col gap-6 px-4 py-6 md:px-8 md:py-8',
              rail && 'lg:flex-row lg:items-start',
            )}
          >
            <div className={cn('min-w-0 flex-1', !fullBleed && 'space-y-6')}>{children}</div>
            {rail}
          </main>
        </div>
        <QuickLogFab />
      </div>
    </HeaderProvider>
  );
}
